import { IOutboundWebhook, IUpdateSettingsRequest } from "shared";
import { When } from "react-if";
import { useMemo, useState } from "react";
import { DateTime } from "luxon";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import { Tooltip } from "@mui/material";
import { ArrowPathIcon } from "@heroicons/react/24/outline";

import { useAllowedFeature } from "../../../../hooks/useAllowedFeature";
import { useSettings } from "../../../../hooks/useSettings";
import { classNames } from "../../../../../common/lib/classNames";
import { Notification } from '../../../../../common/Atoms/Notification';
import { InputTextAddon } from "../../../../../common/Atoms/InputTextAddon";
import { WEBHOOKS_ARTICLE } from "../../../../constants/links";
import { TwoColFormRow } from "../../../Molecules/Form/TwoColFormRow";
import { UpgradeBadge } from "../../../Molecules/UpgradeBadge";
import { OnChangeFn } from "../Main/GeneralSettings";
import { FormLabel } from "../../../../../common/Atoms/FormLabel";
import { Paragraph } from "../../../../../common/Atoms/Typography/Paragraph";
import { Transition } from "../../../../../common/Atoms/Transition";
import { Button } from "../../../../../common/Atoms/Button";
import { Badge } from "../../../../../common/Atoms/Badge";
import {
  useFetchRecentWebhooksQuery,
  useResendWebhookMutation,
  useRetryFailedWebhooksMutation,
} from "../../../../services/api/webhooksApi/webhooks";

import { WebhookEvents } from "./WebhookEvents";

interface WebhooksProps {
  formData: IUpdateSettingsRequest;
  onChange: OnChangeFn;
  onChangeMultiple: (values: Partial<IUpdateSettingsRequest>) => void;
}

const isSuccessCode = (code?: number) => !!code && code >= 200 && code < 300;

export function Webhooks({ formData, onChange, onChangeMultiple }: WebhooksProps) {
  const allowedFeature = useAllowedFeature(`webhooks`);
  const { organisationId } = useParams();

  const { data: recentWebhooksResponse } = useFetchRecentWebhooksQuery(organisationId, {
    skip: !organisationId,
  });

  const [resendWebhook] = useResendWebhookMutation();
  const [retryFailedWebhooks, { isLoading: retryAllLoading }] = useRetryFailedWebhooksMutation();

  const [expandedWebhookRow, setExpandedWebhookRow] = useState<number | null>(null);
  const [resendingId, setResendingId] = useState<number | null>(null);

  const webhooks = useMemo(() => recentWebhooksResponse?.webhooks || [], [ recentWebhooksResponse ]);

  const notification = useMemo(() => {
    if (formData.webhookEnabled === false && formData.webhookUrl) {
      return `There was a problem connecting to your webhook URL and it has been disabled, please set a new webhook URL to enable webhooks again.`;
    }

    return null;
  }, [ formData.webhookEnabled, formData.webhookUrl ]);

  // Requests are grouped into delivery chains (original + its retries). Only the latest
  // attempt of a chain is resendable; earlier attempts are shown as superseded.
  const latestAttemptPerChain = useMemo(() => {
    const chains = new Map<number, IOutboundWebhook>();

    for (const webhook of webhooks) {
      const rootId = webhook.retryForWebhookId || webhook.id;
      const existing = chains.get(rootId);

      if (!existing || new Date(webhook.createdAt) > new Date(existing.createdAt)) {
        chains.set(rootId, webhook);
      }
    }

    return chains;
  }, [ webhooks ]);

  // Chains whose latest attempt failed - matches what "retry failed" will resend
  const failedCount = useMemo(
    () => [ ...latestAttemptPerChain.values() ].filter(w => !isSuccessCode(w.responseCode)).length,
    [ latestAttemptPerChain ],
  );

  const { data: savedSettings } = useSettings();

  // Resends and tests go to the saved URL, so only allow them when the form matches it
  const canResend = allowedFeature && !!savedSettings?.webhookUrl && savedSettings.webhookUrl === formData.webhookUrl;

  function isLatestAttempt(webhook: IOutboundWebhook) {
    return latestAttemptPerChain.get(webhook.retryForWebhookId || webhook.id)?.id === webhook.id;
  }

  function handleChange(e) {
    onChange(e.name, e.value);
  }

  function handleWebhookRowClick(index: number) {
    if (expandedWebhookRow === index) {
      setExpandedWebhookRow(null);
    }
    else {
      setExpandedWebhookRow(index);
    }
  }

  async function handleResend(webhookId: number) {
    if (!organisationId) return;

    setResendingId(webhookId);

    try {
      const result = await resendWebhook({ organisationId, webhookId }).unwrap();

      if (result.success) {
        toast.success(`Webhook resent successfully`);
      }
      else {
        toast.error(`Webhook resend failed${result.statusCode ? ` (status ${result.statusCode})` : ``}`);
      }
    }
    catch {
      toast.error(`Could not resend webhook`);
    }
    finally {
      setResendingId(null);
    }
  }

  async function handleRetryAllFailed() {
    if (!organisationId) return;

    try {
      const result = await retryFailedWebhooks(organisationId).unwrap();

      if (result.retried === 0) {
        toast.info(`No failed webhooks to retry`);
      }
      else if (result.failed === 0) {
        toast.success(`Retried ${result.retried} webhook${result.retried === 1 ? `` : `s`} successfully`);
      }
      else {
        toast.warn(`Retried ${result.retried} webhook${result.retried === 1 ? `` : `s`}: ${result.succeeded} succeeded, ${result.failed} still failing`);
      }
    }
    catch {
      toast.error(`Could not retry failed webhooks`);
    }
  }

  const badge = !allowedFeature && (
    <UpgradeBadge />
  );

  return (
    <TwoColFormRow
      heading={ `Webhooks` }
      headingHelpLink={ WEBHOOKS_ARTICLE }
      badge={ badge }
      help={
        allowedFeature ?
          undefined :
          `Upgrade to "Pro" to enable webhooks.`
      }
    >
      <div className={
        classNames(
          `space-y-2`,
          allowedFeature ? `` : `opacity-50`,
        )
      }>
        <When condition={ !!notification }>
          <Notification
            type={ `warning` }
            title={ `Your webhook has been disabled` }
          >
            { notification }
          </Notification>
        </When>

        <InputTextAddon
          name={ `webhookUrl` }
          value={ formData.webhookUrl || `` }
          label={ `Webhook URL` }
          onChange={ handleChange }
          disabled={ !allowedFeature }
          description={ `The web address where Paidnice will send webhook notifications.` }
          prefix={ `POST` }
        />

        <WebhookEvents
          disabledEvents={ formData.webhookDisabledEvents || [] }
          onChangeMultiple={ onChangeMultiple }
          disabled={ !allowedFeature }
          canSendTest={ canResend }
        />

        { /* TODO: Link to new action page using ID */ }

        <When condition={ webhooks.length > 0 }>
          { () => (
            <>
              <div className={ `flex items-center justify-between pt-2` }>
                <FormLabel>{ `Recent Requests (Last 14 Days)` }</FormLabel>

                <When condition={ failedCount > 0 }>
                  <Button
                    size={ `sm` }
                    onClick={ handleRetryAllFailed }
                    loading={ retryAllLoading }
                    disabled={ !canResend || resendingId !== null }
                  >
                    { `Retry ${failedCount} failed webhook${failedCount === 1 ? `` : `s`}` }
                  </Button>
                </When>
              </div>

              <div className={ `max-h-96 overflow-y-auto` }>
                {
                  webhooks.map((webhook, index) => (
                    <div key={ webhook.id }>
                      <div
                        className={ `grid grid-cols-12 gap-2 items-center border-b border-gray-100 py-1 cursor-pointer` }
                        onClick={ () => handleWebhookRowClick(index) }
                        onKeyDown={ e => {
                          if (e.key === `Enter`) {
                            handleWebhookRowClick(index);
                          }
                        } }
                        role={ `button` }
                        tabIndex={ 0 }
                      >
                        <Paragraph
                          className={ `col-span-3 justify-self-start` }
                          variant={ `note` }
                        >
                          { DateTime.fromJSDate(new Date(webhook.createdAt)).toFormat(`yyyy-LL-dd HH:mm`) }
                        </Paragraph>

                        <Paragraph
                          className={ `col-span-4 justify-self-start truncate` }
                          variant={ `secondary` }
                        >
                          { webhook.destinationUrl }
                        </Paragraph>

                        <div className={ `col-span-2 justify-self-end` }>
                          { !isLatestAttempt(webhook) && (
                            <Tooltip title={ `A newer attempt exists for this delivery - see the row above it` }>
                              <span>
                                <Badge
                                  color={ `gray` }
                                  size={ `xs` }
                                >
                                  { `Retried` }
                                </Badge>
                              </span>
                            </Tooltip>
                          ) }
                          { isLatestAttempt(webhook) && !!webhook.retryForWebhookId && (
                            <Tooltip title={ `This request is a retry of an earlier failed delivery` }>
                              <span>
                                <Badge
                                  color={ `blue` }
                                  size={ `xs` }
                                >
                                  { `Retry` }
                                </Badge>
                              </span>
                            </Tooltip>
                          ) }
                        </div>

                        <Paragraph
                          className={ classNames(
                            `col-span-2 justify-self-end`,
                            isSuccessCode(webhook.responseCode) ? `text-green-600` : `text-red-600`,
                          ) }
                          variant={ `secondary` }
                        >
                          { webhook.responseCode || `ERR` }
                        </Paragraph>

                        <div className={ `col-span-1 justify-self-end` }>
                          { isLatestAttempt(webhook) && (
                            <Tooltip title={ `Resend to your current webhook URL` }>
                              <span>
                                <button
                                  type={ `button` }
                                  disabled={ !canResend || (resendingId !== null && resendingId !== webhook.id) || retryAllLoading }
                                  onClick={ e => {
                                    e.stopPropagation();
                                    handleResend(webhook.id);
                                  } }
                                  className={ `p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50 disabled:hover:text-gray-400` }
                                >
                                  <ArrowPathIcon
                                    className={ classNames(
                                      `h-4 w-4`,
                                      resendingId === webhook.id ? `animate-spin` : ``,
                                    ) }
                                  />
                                </button>
                              </span>
                            </Tooltip>
                          ) }
                        </div>
                      </div>

                      <Transition
                        show={ expandedWebhookRow === index && !!webhook.payload }
                        speed={ `slow` }
                      >
                        <pre className={ `flex justify-start bg-slate-600 dark:bg-[#475569] text-white text-xs` }>
                          <code className={ `whitespace-pre-wrap text-left` }>
                            { JSON.stringify(webhook.payload, null, 2) }
                          </code>
                        </pre>
                      </Transition>
                    </div>
                  ))
                }
              </div>
            </>
          ) }

        </When>
      </div>
    </TwoColFormRow>
  );
}
